import * as React from 'react'

import {fetchLanguages} from '@frontrow/console/src/entities/models/language'
import {Languages, type LangT} from '@frontrow/console/src/entities/helpers/languages'
import {exhaustive} from '@freckle/exhaustive'
import map from 'lodash/map'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import {
  createSelectorOption,
  type SelectorOptionT,
  createSelectorGroupAsItem
} from '@frontrow/console/src/components/generic-selector'

import {SelfContainedTeacherSelector} from '@frontrow/console/src/components/self-contained-teacher-selector'
import {patchTeacherById} from '@frontrow/console/src/helpers/api-teachers'

type UpdateModeT =
  | {
      tag: 'component-performs-update'
      teacherId: number
    }
  | {
      tag: 'component-is-passthrough'
    }

type ComponentLabelsT = {
  formLabel?: string | null
  loadingText: string
}

/** @internal */
export type Props = {
  updateMode: UpdateModeT
  labels: ComponentLabelsT
  onError?: (error: Error) => void
  selectedLanguage: LangT
  selectorClassName?: string | null
  dataTest?: string
  filterLanguages?: LangT[]
  onChange: (language: LangT) => void
}

export function LanguageDropdown({
  selectedLanguage: selectedItem,
  onChange: onChangeSelection,
  updateMode,
  labels,
  onError,
  dataTest,
  selectorClassName,
  filterLanguages
}: Props): React.ReactElement {
  const onChangeSelectionP = (selectedSetting: LangT) => {
    switch (updateMode.tag) {
      case 'component-is-passthrough':
        return Promise.resolve(onChangeSelection(selectedSetting))
      case 'component-performs-update':
        return setTeacherLanguage(updateMode.teacherId, selectedSetting)
          .then(() => onChangeSelection(selectedSetting))
          .catch((err: unknown) => {
            if (!onError) throw err
            onError(err instanceof Error ? err : new Error(String(err)))
          })
      default:
        return exhaustive(updateMode as never, 'UpdateModeT')
    }
  }

  const fetchObj = React.useMemo(() => ({tag: 'be-fetched' as const, fetch: fetchLanguages}), [])

  const itemsToCollection = (languages: {language: LangT; name: string}[]) => {
    const invalidOptions = filterLanguages ?? []

    const validLanguageOptions = filter(
      languages,
      language => !includes(invalidOptions, language.language)
    )

    const options = map(validLanguageOptions, itemToSelectorOption)

    const optGroup = createSelectorGroupAsItem(
      'lang-dropdown-optgroup',
      'Language settings only apply to math content and navigation',
      options
    )

    return [optGroup]
  }

  return (
    <SelfContainedTeacherSelector
      id="teacher-language-selector"
      labels={labels}
      selectedItem={selectedItem}
      itemsToCollection={itemsToCollection}
      onChangeSelection={onChangeSelectionP}
      dataTest={dataTest}
      fetchItems={fetchObj}
      selectorClassName={selectorClassName}
      onFetchError={onError}
    />
  )
}

const setTeacherLanguage = (teacherId: number, language: LangT): Promise<unknown> =>
  patchTeacherById(teacherId, {language})

function itemToSelectorOption({
  language,
  name
}: {
  language: LangT
  name: string
}): SelectorOptionT<LangT> {
  return createSelectorOption(
    language,
    name,
    false,
    `language-option-${Languages.toString(language)}`
  )
}
